import { PropsWithChildren, ReactElement, useEffect, useState, useRef } from "react";
import useWindowWidth from "../../../hooks/UseWindowWidth";
import FilterItem, { FilterItemModel } from "./FilterItem";
import { className } from "../../../shared/ClassName";
import debounce from "../../../shared/Debounce";
import isBrowser from "../../../shared/IsBrowser";
import { searchIcon, allIcon, chevron } from "../../Icons";
import * as styles from "./Filters.module.scss";

export class FilterModel {
    constructor(public readonly id: string, public readonly label: string = "", public readonly items: FilterItemModel[], public readonly queryStringKey?: string | null) {}
}

interface IFiltersProps {
    content: GatsbyTypes.strapi_ComponentSharedFiltersContent;
    filterModels: FilterModel[];
    onChange: (searchText: string, filters: Map<string, FilterItemModel[]>) => void;
    onFilterChange?: (filterId: string, value: string) => void;
}

function updateQueryString(queryString: string): void {
    if (!isBrowser()) return;

    const newUrl =
        queryString !== ""
            ? `${window.location.protocol}//${window.location.host}${window.location.pathname}?${queryString}`
            : `${window.location.protocol}//${window.location.host}${window.location.pathname}`;

    window.history.pushState({ path: newUrl }, "", newUrl);
}

function filtersChanged(newFilters: Map<string, FilterItemModel[]>, existingFilters: Map<string, FilterItemModel[]>): boolean {
    if (newFilters.size !== existingFilters.size) return true;

    for (const [key, newItems] of newFilters) {
        const existingItems = existingFilters.get(key);
        if (!existingItems || newItems.length !== existingItems.length || !newItems.every((x, i) => x === existingItems[i])) return true;
    }

    return false;
}

export default function Filters(props: PropsWithChildren<IFiltersProps>): ReactElement {
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState<Map<string, FilterItemModel[]>>(new Map<string, FilterItemModel[]>());
    const isDesktop = useWindowWidth(769);
    const debouncedSearchText = useRef(
        debounce((text: string, filters: Map<string, FilterItemModel[]>) => {
            props.onChange(text, filters);
            if (text !== "" && props.onFilterChange) props.onFilterChange("searchText", text);
        }, 1000)
    );

    const search = isBrowser() ? window.location.search : "";

    useEffect(() => {
        if (props.filterModels.length === 0) return;

        const newSelectedFilters = new Map<string, FilterItemModel[]>();
        const urlParams = new URLSearchParams(search.toLowerCase());

        for (const filter of props.filterModels) {
            const queryStringValues = urlParams.get(filter.queryStringKey ?? filter.id)?.split(";") ?? [];
            if (queryStringValues.length === 0) continue;
            const filterItems = filter.items.filter(x => queryStringValues.includes(x.getTextForQueryString()));
            newSelectedFilters.set(filter.id, filterItems);
        }

        if (filtersChanged(newSelectedFilters, selectedFilters)) {
            setSelectedFilters(newSelectedFilters);
            props.onChange(searchText, newSelectedFilters);
        }

        setLoading(false);
    }, [props.filterModels, search]);

    useEffect(() => {
        if (loading || !isDesktop) return;

        const urlParams = new URLSearchParams();

        for (const filter of props.filterModels) {
            const selectedFilter = selectedFilters.get(filter.id);
            if (!selectedFilter) continue;

            urlParams.set(filter.queryStringKey ?? filter.id, selectedFilter.map(x => x.getTextForQueryString()).join(";"));
        }

        updateQueryString(urlParams.toString());
    }, [selectedFilters, loading, isDesktop]);

    function handleTextChange(value: string) {
        setSearchText(value);
        if (isDesktop) debouncedSearchText?.current(value, selectedFilters);
    }

    function handleSelectionChange(filterId: string, item: FilterItemModel) {
        const newSelectedFilters = new Map(selectedFilters);
        let currentFilter = newSelectedFilters.get(filterId);

        if (item.id !== "") {
            if (!currentFilter) {
                currentFilter = [];
                newSelectedFilters.set(filterId, currentFilter);
            }

            const index = currentFilter.findIndex(x => x.id === item.id);
            if (index < 0) {
                currentFilter.push(item);
                if (props.onFilterChange) props.onFilterChange(filterId, item.text);
            } else currentFilter.splice(index, 1);

            if (currentFilter.length === 0) newSelectedFilters.delete(filterId);
        } else {
            if (!currentFilter) return;
            newSelectedFilters.delete(filterId);
        }

        setSelectedFilters(newSelectedFilters);
        if (isDesktop) props.onChange(searchText, newSelectedFilters);
    }

    function handleApplyClick() {
        props.onChange(searchText, selectedFilters);
        setFiltersVisible(false);
    }

    const allFilterItem = new FilterItemModel("", "All", allIcon);

    return (
        <aside className={styles.filters}>
            {isDesktop ? (
                <h2>{props.content.headingText}</h2>
            ) : (
                <button className={styles.filtersBtn} onClick={() => setFiltersVisible(!filtersVisible)}>
                    {props.content.headingText} {chevron.down2}
                </button>
            )}

            <div {...className(styles.filtersItems, !isDesktop && filtersVisible ? styles.isVisible : "")}>
                {!isDesktop && (
                    <>
                        <button className={styles.closeFilters} onClick={() => setFiltersVisible(false)}>
                            {props.content.headingText} {chevron.up2}
                        </button>
                        <button className={styles.applyFilters} onClick={handleApplyClick}>
                            {props.content.applyButtonText}
                        </button>
                    </>
                )}

                <div className={styles.searchInput}>
                    <input type="text" placeholder={props.content.searchLabel} value={searchText} onChange={e => handleTextChange(e.target.value)} autoFocus />
                    <div className={styles.searchIcon}>{searchIcon}</div>
                </div>

                {props.filterModels &&
                    props.filterModels.map(x => (
                        <div key={x.id} className={styles.filterItem}>
                            <h3>{x.label}</h3>
                            <div>
                                <FilterItem model={allFilterItem} onSelectionChange={() => handleSelectionChange(x.id, allFilterItem)} isSelected={!selectedFilters.get(x.id)} />
                                {x.items.map(y => (
                                    <FilterItem key={y.id} model={y} onSelectionChange={() => handleSelectionChange(x.id, y)} isSelected={selectedFilters.get(x.id)?.includes(y) ?? false} />
                                ))}
                            </div>
                        </div>
                    ))}

                {props.children}
            </div>
        </aside>
    );
}
